// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useMemo, useState } from 'react';
import styles from "./styles";
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { Box, Button, withStyles } from '@material-ui/core';
import { useIntl } from "react-intl";
import { getI18n, getI18nFormattedMessage } from "../../utils/intl";
import { getAuditLogs } from "../../actions/auditLogs"
import PropTypes, { bool, func, object, string } from 'prop-types';
import ListContainer from "../../pages/list"
import { getAuditLogsIsLoading, getAuditLogsListResults, getAuditLogsListTotalElements, getAuditLogsListTotalPages } from '../../reducers/auditLogs';
import { AUDIT_LOGS_SORT_BY_ASCENDING, AUDIT_LOGS_SORT_BY_DESCENDING, GRID_ROWS_PER_PAGE_DEFAULT_OPTION } from '../../constants';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import { Filter, convertDateToMilliSeconds, timeFormat } from './controls';
import { FilterByDate } from "../Requests/List/controls"
import { getIsPortalAdmin } from '../../reducers/portalConfig';
import { push } from 'connected-react-router';
import { isUndefined } from 'lodash';

export const TimeHeader = ({ classes, label, handleSort, decideSort }) => (
  <div className={classes.dropIconContainer}>
    <div>
      {label}
    </div>
    <div onClick={handleSort}>
      {
        decideSort === false ? <ArrowDropDown className={classes.dropIcon} /> :
          decideSort === true ? <ArrowDropUp className={classes.dropIcon} /> : ""
      }
    </div>
  </div>
);

TimeHeader.propTypes = {
  classes: object,
  label: object,
  handleSort: func,
  decideSort: bool,
}

export const getAuditLogsColumns = ({ classes }, handleSort, decideSort) => [{
  id: 'actionTs',
  label: (
    <TimeHeader decideSort={decideSort} handleSort={handleSort} classes={classes} label={getI18nFormattedMessage('label.auditlogs.time')} />
  ),
  minWidth: 150,
  value: (item) => timeFormat(item.actionTs),
},
{
  id: 'entityType',
  label: getI18nFormattedMessage('label.auditlogs.object'),
  minWidth: 150,
},
{
  id: 'entityName',
  label: getI18nFormattedMessage('label.auditlogs.objectname'),
  minWidth: 150,
},
{
  id: 'action',
  label: getI18nFormattedMessage('label.product.tiers.list.column.action'),
  minWidth: 150,
},
{
  id: 'userName',
  label: getI18nFormattedMessage('label.auditlogs.user'),
  minWidth: 150,
},
{
  id: 'simplifiedUserAgent',
  label: getI18nFormattedMessage('label.auditlogs.agent'),
  minWidth: 150,
},
];

export const AuditLogs = (props) => {
  const {
    getAuditLogs,
    totalElements = 0,
    totalPages,
    results = [],
    defaultSelectedRows = [],
    classes,
    isLoading,
    isPortalAdmin,
    push,
  } = props

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [decideSort, setDecideSort] = useState(false);
  const [selectedRows, setSelectedRows] = useState(defaultSelectedRows);
  const [filterByStartDate, setFilterByStartDate] = useState('');
  const [filterByEndDate, setFilterByEndDate] = useState('');
  const [filterByObject, setFilterByObject] = useState('');
  const [filterByObjectName, setFilterByObjectName] = useState('');
  const [filterByUserName, setFilterByUserName] = useState('');
  const [filterByActions, setFilterByActions] = useState('');
  const [filterByAnyApplied, setFilterByAnyApplied] = useState(false);
  const [isDisableClearAllButton, setIsDisableClearAllButton] = useState(true);
  const [appliedFilterByObject, setAppliedFilterByObject] = useState('');
  const [appliedFilterByObjectName, setAppliedFilterByObjectName] = useState('');
  const [appliedFilterByUserName, setAppliedFilterByUserName] = useState('');
  const [appliedFilterByActions, setAppliedFilterByActions] = useState('');

  const sorting = decideSort === true ? AUDIT_LOGS_SORT_BY_ASCENDING :
    AUDIT_LOGS_SORT_BY_DESCENDING;

  const convertedStartDate = filterByStartDate && convertDateToMilliSeconds(filterByStartDate);
  const convertedEndDate = filterByEndDate && convertDateToMilliSeconds(filterByEndDate);

  const [payload, setPayload] = useState({
    sort: sorting,
    page: page,
    size: rowsPerPage,
    entityType: filterByObject,
    startTs: convertedStartDate,
    endTs: convertedEndDate,
    entityName: filterByObjectName,
    userName: filterByUserName,
    action: filterByActions,
  })

  const intl = getI18n(useIntl());

  useMemo(() => {
    if (
      filterByStartDate === "" &&
      filterByEndDate === "" &&
      filterByObject === "" &&
      filterByObjectName === "" &&
      filterByUserName === "" &&
      filterByActions === ""
    ) {
      setIsDisableClearAllButton(true);
    } else {
      setIsDisableClearAllButton(false);
    }

  }, [filterByStartDate, filterByEndDate, filterByObject, filterByObjectName,
    filterByUserName, filterByActions, isDisableClearAllButton]);

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    setDecideSort(false);
    setPayload({ ...payload, page: newPage })
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setDecideSort(false)
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setPayload({ ...payload, size: newRowsPerPage })
  };

  const handleSort = () => {
    setDecideSort(!decideSort)
  }

  const onFilterByChange = (value, filterValueType) => {
    switch (filterValueType) {
      case "object":
        setFilterByObject(value);
        break;
      case "objectName":
        setFilterByObjectName(value);
        break;
      case "userName":
        setFilterByUserName(value);
        break;
      case "actions":
        setFilterByActions(value);
        break;
      default:
        break;
    }
  };

  const appliedFilterAndSetPayload = (filterValueType) => {
    switch (filterValueType) {
      case "object":
        if (filterByObject !== appliedFilterByObject) {
          setPayload({
            ...payload,
            entityType: filterByObject,
          })
          setPage(0);
          setAppliedFilterByObject(filterByObject);
        }
        break;
      case "objectName":
        if (filterByObjectName !== appliedFilterByObjectName) {
          setPayload({
            ...payload,
            entityName: filterByObjectName,
          })
          setPage(0);
          setAppliedFilterByObjectName(filterByObjectName);
        }
        break;
      case "userName":
        if (filterByUserName !== appliedFilterByUserName) {
          setPayload({
            ...payload,
            userName: filterByUserName,
          })
          setPage(0);
          setAppliedFilterByUserName(filterByUserName);
        }
        break;
      case "actions":
        if (filterByActions !== appliedFilterByActions) {
          setPayload({
            ...payload,
            action: filterByActions,
          })
          setPage(0);
          setAppliedFilterByActions(filterByActions);
        }
        break;
      default:
        break;
    }
  }

  const onFilterByKeyPress = (e, filterValueType) => {
    if (e.key === "Enter") {
      appliedFilterAndSetPayload(filterValueType);
    }
  };

  const handleBlur = (filterValueType) => {
    appliedFilterAndSetPayload(filterValueType);
  };

  const handleClearAllFilters = () => {
    setFilterByAnyApplied(false);
    setFilterByObject("")
    setFilterByUserName("")
    setFilterByObjectName("");
    setFilterByStartDate("");
    setFilterByEndDate("");
    setFilterByActions("")
    setPage(0);
    setPayload({ sort: sorting, page: 0, size: rowsPerPage })
  };

  const onFilterByStartDateChange = (date) => {
    if (date) {
      if ((isUndefined(filterByStartDate.getTime)) || (typeof filterByStartDate.getTime !== 'function') ||
        (date.getTime() !== filterByStartDate?.getTime())) {
        setFilterByStartDate(date);
        setPayload({
          ...payload,
          startTs: convertedStartDate,
        });
        setPage(0);
      }

    }
  }

  const onFilterByEndDateChange = (date) => {
    if (date) {
      if ((isUndefined(filterByEndDate.getTime)) || (typeof filterByEndDate.getTime !== 'function') ||
        (date.getTime() !== filterByEndDate?.getTime())) {
        setFilterByEndDate(date);
        setPayload({
          ...payload,
          endTs: convertedEndDate,
        })
        setPage(0);
      }
    }
  }

  const onClearFilterByDate = () => {
    if (filterByStartDate || filterByEndDate) {
      setFilterByStartDate("");
      setFilterByEndDate("");
      setPayload({
        ...payload,
        startTs: "",
        endTs: "",
      })
    }
  }

  useEffect(() => {
    getAuditLogs({
      sort: sorting,
      page: page,
      size: rowsPerPage,
      entityType: filterByObject,
      startTs: convertedStartDate,
      endTs: convertedEndDate,
      entityName: filterByObjectName,
      userName: filterByUserName,
      action: filterByActions,
    })
  }, [decideSort, payload])

  useEffect(() => {
    if (!isPortalAdmin) {
      push('/404')
    }
  }, [isPortalAdmin])

  useEffect(() => {
    if (payload.entityType !== "" || payload.entityName !== "" || payload.startTs !== ""
      || payload.endTs !== "" || payload.userName !== "" || payload.action !== "") {
      setFilterByAnyApplied(true)
    } else {
      setFilterByAnyApplied(false)
    }

  }, [payload])

  const noResultsMessage = filterByAnyApplied ?
    intl.getI18nMessage('label.auditlogs.filter.no.results') :
    intl.getI18nMessage('label.auditlogs.no.results');

  return (
    <ListContainer
      listPageId='audit-logs-list-page'
      isLoading={isLoading}
      filterAndSortContent={(
        <>
          <Box className={classes.flexWrap}>
            <Box>
              <Filter
                id='audit-logs-filter-by-object'
                fieldContainerClass={classes.fieldContainer}
                name={intl.getI18nMessage('label.filter')}
                value={filterByObject}
                placeholder={intl.getI18nMessage('label.auditlogs.object')}
                handleChange={(e) => onFilterByChange(e, "object")}
                onKeyPress={(e) => onFilterByKeyPress(e, "object")}
                handleBlur={() => handleBlur("object")}
              />
            </Box>
            <Box>
              <Filter
                id='audit-logs-filter-by-object-name'
                fieldContainerClass={classes.fieldObjectContainer}
                value={filterByObjectName}
                placeholder={intl.getI18nMessage('label.auditlogs.objectname')}
                handleChange={(e) => onFilterByChange(e, "objectName")}
                onKeyPress={(e) => onFilterByKeyPress(e, "objectName")}
                handleBlur={() => handleBlur("objectName")}
              />
            </Box>
            <Box>
              <Filter
                id='audit-logs-filter-by-action'
                fieldContainerClass={classes.fieldObjectContainer}
                value={filterByActions}
                placeholder={intl.getI18nMessage('label.product.tiers.list.column.action')}
                handleChange={(e) => onFilterByChange(e, "actions")}
                onKeyPress={(e) => onFilterByKeyPress(e, "actions")}
                handleBlur={() => handleBlur("actions")}
              />
            </Box>
            <Box>
              <Filter
                id='audit-logs-filter-by-user'
                fieldContainerClass={classes.fieldObjectContainer}
                value={filterByUserName}
                placeholder={intl.getI18nMessage('label.auditlogs.user')}
                handleChange={(e) => onFilterByChange(e, "userName")}
                onKeyPress={(e) => onFilterByKeyPress(e, "userName")}
                handleBlur={() => handleBlur("userName")}
              />
            </Box>
            <Box>
              <FilterByDate
                containerClass={classes.dateRangeContainerClass}
                startDate={filterByStartDate}
                onStartDateChange={onFilterByStartDateChange}
                endDate={filterByEndDate}
                onEndDateChange={onFilterByEndDateChange}
                onClear={onClearFilterByDate}
                startDatePlaceholder={intl.getI18nMessage("label.daterangepicker.start.date.placeholder")}
                endDatePlaceholder={intl.getI18nMessage("label.daterangepicker.end.date.placeholder")}
              />
            </Box>
            <Box>
              <Button
                data-apim-test='clear-all-button'
                variant='outlined'
                className={classes.clearAllButton}
                onClick={() => handleClearAllFilters()}
                disabled={isDisableClearAllButton}
              >
                {intl.getI18nMessage("label.auditlogs.clear.all.button")}
              </Button>
            </Box>
          </Box>
        </>
      )}
      columns={getAuditLogsColumns(props, handleSort, decideSort)}
      rows={results}
      noResultsMessage={noResultsMessage}
      totalElements={totalElements}
      totalPages={totalPages}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onChangePage={onChangePage}
      onChangePreviousPage={onChangePreviousPage}
      onChangeNextPage={onChangeNextPage}
      page={page}
      selectedRows={selectedRows}
      setSelectedRows={setSelectedRows}
      pageHeaderTitle={intl.getI18nMessage("label.auditlogs.title")}
      rowsPerPage={rowsPerPage}
    />
  )
}

const mapStateToProps = (state) => {
  return {
    auditLogs: state?.auditLogs,
    totalPages: getAuditLogsListTotalPages(state),
    totalElements: getAuditLogsListTotalElements(state),
    results: getAuditLogsListResults(state),
    isLoading: getAuditLogsIsLoading(state),
    isPortalAdmin: getIsPortalAdmin(state),
  };
};

const mapDispatchToProps = {
  getAuditLogs,
  push,
}

AuditLogs.propTypes = {
  getAuditLogs: PropTypes.func,
  auditLogs: PropTypes.object,
  totalPages: PropTypes.number,
  totalElements: PropTypes.number,
  results: PropTypes.arrayOf(object),
  defaultSelectedRows: PropTypes.arrayOf(string),
  classes: PropTypes.object,
  isLoading: PropTypes.bool,
  isPortalAdmin: PropTypes.bool,
  push: PropTypes.func,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(AuditLogs);
